// Label Configuration for Healthcare System Terminology
// Provides human-readable labels and help text for all system classifiers

export const SYSTEM_TYPE_LABELS = {
    tax_nhs: {
        label: "Tax-Funded NHS Model",
        help: "Healthcare funded through general taxation with direct government provision of services (like the UK's National Health Service)."
    },
    single_payer: {
        label: "Single-Payer Claims",
        help: "Government acts as sole insurer, paying private providers for services (like Canada's Medicare system)."
    },
    bismarck_funds: {
        label: "Bismarck / Sickness Funds",
        help: "Employment-based mandatory insurance through competing non-profit funds (like Germany's system)."
    },
    mixed_public_private: {
        label: "Mixed Public/Private",
        help: "Combination of public and private insurance with varying levels of integration."
    },
    two_tier: {
        label: "Two-Tier System",
        help: "Separate public and private systems operating in parallel with different access levels."
    },
    savings_stack: {
        label: "Savings Stack Model",
        help: "Mandatory health savings accounts combined with catastrophic insurance (like Singapore's system)."
    }
};

export const PRESSURE_VALVE_LABELS = {
    waiting: {
        label: "Waiting Lists",
        help: "Rationing through queues and wait times for non-urgent procedures to control demand."
    },
    price_discipline: {
        label: "Price Regulation",
        help: "Government-set prices or reference pricing to control healthcare costs."
    },
    budget_cap: {
        label: "Global Budgets",
        help: "Fixed annual budgets for healthcare facilities or regions to limit total spending."
    },
    cost_sharing: {
        label: "Patient Cost-Sharing",
        help: "Out-of-pocket fees, copayments, or deductibles to reduce demand and share costs."
    },
    scope_limits: {
        label: "Limited Benefits Basket",
        help: "Defined list of covered services, excluding certain treatments or medications."
    },
    private_tier: {
        label: "Private Parallel Tier",
        help: "Private insurance or pay options that bypass public system constraints."
    },
    capacity_limit: {
        label: "Supply Controls",
        help: "Restrictions on number of providers, facilities, or medical school slots to control costs."
    },
    admin_gatekeeping: {
        label: "Administrative Gatekeeping",
        help: "Required referrals, prior authorizations, or bureaucratic hurdles to access care."
    }
};

export const TRAIT_LABELS = {
    defined_benefits_basket: {
        label: "Defined Benefits Basket",
        help: "Explicit list of covered services and treatments, with clear exclusions."
    },
    drug_price_negotiation: {
        label: "Drug Price Negotiation",
        help: "Government or insurer negotiates pharmaceutical prices directly with manufacturers."
    },
    gatekeeping_primary_care: {
        label: "Primary Care Gatekeeping",
        help: "Requires referral from general practitioner to access specialist care."
    },
    global_budget: {
        label: "Global Budget",
        help: "Fixed total budget allocated to healthcare providers or regions annually."
    },
    high_cost_sharing: {
        label: "High Cost-Sharing",
        help: "Significant out-of-pocket payments required from patients for services."
    },
    mandatory_coverage: {
        label: "Mandatory Coverage",
        help: "Legal requirement for all residents to have health insurance."
    },
    multi_payer_rulebook: {
        label: "Multi-Payer Rulebook",
        help: "Multiple insurers operating under standardized regulations and pricing rules."
    },
    national_fee_schedule: {
        label: "National Fee Schedule",
        help: "Government-set standardized prices for medical services across all providers."
    },
    negotiated_fees: {
        label: "Negotiated Fees",
        help: "Prices determined through bargaining between insurers and providers."
    },
    private_parallel_tier: {
        label: "Private Parallel Tier",
        help: "Separate private insurance market operating alongside public system."
    },
    private_supplemental_common: {
        label: "Private Supplemental Insurance",
        help: "Additional private insurance to cover gaps or enhance public coverage."
    },
    regionalized_delivery: {
        label: "Regionalized Delivery",
        help: "Healthcare organized and budgeted at regional rather than national level."
    },
    risk_equalization: {
        label: "Risk Equalization",
        help: "Transfers between insurers to compensate for differences in patient health risk."
    },
    single_payer_claims: {
        label: "Single-Payer Claims Processing",
        help: "One government entity processes all healthcare payment claims."
    },
    strong_digital_claims: {
        label: "Strong Digital Infrastructure",
        help: "Advanced electronic health records and claims processing systems."
    },
    universal_resident: {
        label: "Universal Resident Coverage",
        help: "Healthcare coverage guaranteed to all legal residents regardless of employment."
    }
};

// Helper function to get label with fallback
export const getSystemTypeLabel = (key) => {
    return SYSTEM_TYPE_LABELS[key]?.label || key?.replace(/_/g, ' ') || 'Unknown';
};

export const getPressureValveLabel = (key) => {
    return PRESSURE_VALVE_LABELS[key]?.label || key?.replace(/_/g, ' ') || 'Unknown';
};

export const getTraitLabel = (key) => {
    return TRAIT_LABELS[key]?.label || key?.replace(/_/g, ' ') || 'Unknown';
};

export const getSystemTypeHelp = (key) => {
    return SYSTEM_TYPE_LABELS[key]?.help || '';
};

export const getPressureValveHelp = (key) => {
    return PRESSURE_VALVE_LABELS[key]?.help || '';
};

export const getTraitHelp = (key) => {
    return TRAIT_LABELS[key]?.help || '';
};

// Color mappings (keeping existing colors)
export const PRESSURE_VALVE_COLORS = {
    waiting: "#eab308",
    price_discipline: "#3b82f6",
    budget_cap: "#ef4444",
    cost_sharing: "#f97316",
    scope_limits: "#a855f7",
    private_tier: "#10b981",
    capacity_limit: "#6366f1",
    admin_gatekeeping: "#64748b"
};
